<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from "vue";

import IconClose from "@shared/ui/icons/IconClose.vue";

import { useMediaSource } from "@features/file-transfer/useMediaSource";

import type { FileAttachment } from "shared";

// Лайтбокс картинки. Пока оригинал качается/расшифровывается — превью из
// метаданных, растянутое до конечных габаритов (без скачка размеров), с
// прогрессом. Паттерны (фон, крестик, Esc, анимация) — как у VideoPlayerModal.
const props = defineProps<{ file: FileAttachment; visible: boolean }>();
const emit = defineEmits<{ close: [] }>();

const { get: getMedia, load: loadMedia } = useMediaSource();
const media = getMedia(props.file);

// Конечные габариты из метаданных: аспект, вписанный в вьюпорт. Иначе превью
// (маленький jpeg) показывалось бы в своём натуральном размере.
const box = computed(() => {
    const { width, height } = props.file;
    if (!width || !height) {
        return undefined;
    }
    return {
        aspectRatio: `${width} / ${height}`,
        height: `min(calc(100vh - 64px), calc(min(1280px, 100vw - 64px) * ${(
            height / width
        ).toFixed(4)}), ${height}px)`,
    };
});

watch(
    () => props.visible,
    (visible) => {
        if (visible) {
            void loadMedia(props.file);
            document.addEventListener("keydown", onKeydown);
        } else {
            document.removeEventListener("keydown", onKeydown);
        }
    },
    { immediate: true }
);

function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
        emit("close");
    }
}

onBeforeUnmount(() => {
    document.removeEventListener("keydown", onKeydown);
});
</script>

<template>
    <Teleport to="body">
        <Transition name="mc-il">
            <div v-if="visible" class="mc-il" @click="emit('close')">
                <button
                    class="mc-il__close"
                    type="button"
                    aria-label="Закрыть"
                    @click.stop="emit('close')"
                >
                    <IconClose />
                </button>

                <div class="mc-il__box" :style="box" @click.stop>
                    <img
                        v-if="media.url || file.poster"
                        :src="media.url ?? file.poster"
                        :alt="file.name"
                        class="mc-il__img"
                        draggable="false"
                    />
                    <span v-if="!media.url" class="mc-il__progress">
                        <template v-if="media.error">
                            Не удалось загрузить
                        </template>
                        <template v-else>
                            {{ Math.round(media.progress * 100) }}%
                        </template>
                    </span>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.mc-il {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
}

.mc-il__box {
    position: relative;
    max-width: min(1280px, 100%);
    max-height: calc(100vh - 64px);
    background: #000;
    cursor: default;
}

.mc-il__img {
    display: block;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: calc(100vh - 64px);
    object-fit: contain;
}

.mc-il__progress {
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 14px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-family: var(--mc-mono);
    font-size: 13px;
}

.mc-il__close {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: rgba(0, 0, 0, 0.55);
    border: none;
    color: #fff;
    cursor: pointer;
    transition: color 0.12s ease;
}

.mc-il__close:hover {
    color: var(--mc-acid);
}

/* Анимация открытия/закрытия — как у видеоплеера. */
.mc-il-enter-active,
.mc-il-leave-active {
    transition: opacity 0.18s ease;
}

.mc-il-enter-active .mc-il__box,
.mc-il-leave-active .mc-il__box {
    transition:
        transform 0.18s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.18s ease;
}

.mc-il-enter-from,
.mc-il-leave-to {
    opacity: 0;
}

.mc-il-enter-from .mc-il__box,
.mc-il-leave-to .mc-il__box {
    transform: scale(0.96);
    opacity: 0;
}
</style>
